import { Server, Circle, Activity, FileText, Loader2, AlertTriangle } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useWorkers } from '@/hooks/useApi'

type WorkerItem = {
  name: string
  ecosystem: string
  classification?: 'internal' | 'external'
  classification_source?: string
  classification_status?: 'classified'
  health_url: string | null
  internal: boolean
  status: string
  http?: number
  version?: string | null
  latency_ms?: number | null
  error?: string
}

const STATUS_DOT: Record<string, string> = {
  healthy:    'text-success',
  degraded:   'text-warning',
  unreachable:'text-danger',
  internal:   'text-text-muted',
  unknown:    'text-text-muted',
}

function statusDotClass(status: string) {
  return STATUS_DOT[status] ?? 'text-text-muted'
}

function EcosystemGroup({
  label,
  workers,
}: {
  label: string
  workers: WorkerItem[]
}) {
  const healthy = workers.filter(w => w.status === 'healthy').length
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between py-1">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">{label}</span>
        <Badge variant="muted" className="text-[10px]">{healthy}/{workers.length}</Badge>
      </div>
      {workers.map(w => (
        <div
          key={w.name}
          className="flex items-center gap-2 px-3 py-2 rounded-lg glass-card min-h-[44px]"
        >
          {/* Status dot */}
          <Circle className={cn('h-2 w-2 shrink-0 fill-current', statusDotClass(w.status))} />

          {/* Name + badges */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-sm font-semibold text-text-primary truncate">{w.name}</span>
              <Badge variant="outline" className="text-[9px] px-1 py-0 h-4 shrink-0">{label}</Badge>
              {w.internal && (
                <Badge variant="muted" className="text-[9px] px-1 py-0 h-4 shrink-0">internal</Badge>
              )}
              {w.version && (
                <span className="text-[10px] text-text-muted shrink-0">v{w.version}</span>
              )}
              {w.latency_ms != null && (
                <span className="text-[10px] text-text-muted shrink-0">{w.latency_ms}ms</span>
              )}
            </div>
          </div>

          {/* Action buttons — AD-015: always visible, never hover-only */}
          {!w.internal && (
            <div className="flex items-center gap-1 shrink-0">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                title="Health Check"
                onClick={() => w.health_url && window.open(w.health_url, '_blank')}
              >
                <Activity className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                title="Logs"
                onClick={() => {/* Logs panel integration */}}
              >
                <FileText className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

export function WorkersPanel() {
  const { data, isLoading } = useWorkers()

  const ecosystems = data?.ecosystems ?? {}
  const labels = data?.ecosystem_labels ?? {}
  const internalKeys = ['opsbridge', 'cora', 'mail-organiser', 'workerscan'] as const
  const externalWorkers: WorkerItem[] = ecosystems.bakersguild ?? []
  const violations = data?.classification_violations ?? []

  const lastCheck = data?.last_check
    ? new Date(data.last_check).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : null

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Server className="h-4 w-4" />
              Platform Workers
              {isLoading && <Loader2 className="h-3 w-3 animate-spin text-text-muted" />}
            </CardTitle>
            <div className="flex items-center gap-2">
              {data && (
                <Badge variant={data.healthy === data.total ? 'success' : 'warning'} className="text-xs">
                  {data.healthy}/{data.total} healthy
                </Badge>
              )}
              {lastCheck && <span className="text-[10px] text-text-muted">{lastCheck}</span>}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {isLoading ? (
            <div className="text-sm text-text-muted py-4 text-center">Loading workers...</div>
          ) : (
            internalKeys.map(key => (
              <EcosystemGroup
                key={key}
                label={labels[key] || key}
                workers={(ecosystems[key] ?? []) as WorkerItem[]}
              />
            ))
          )}
        </CardContent>
      </Card>

      {externalWorkers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Server className="h-4 w-4" />
              External Systems
            </CardTitle>
          </CardHeader>
          <CardContent>
            <EcosystemGroup
              label={labels.bakersguild || 'BakersGuild (External)'}
              workers={externalWorkers}
            />
          </CardContent>
        </Card>
      )}

      {violations.length > 0 && (
        <Card className="border-warning/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-warning">
              <AlertTriangle className="h-4 w-4" />
              Worker Classification Violations
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-xs text-text-muted">
              These workers were created outside the governed ecosystem-registration path and must be classified before normal platform use.
            </p>
            {violations.map(v => (
              <div key={v.name} className="rounded-lg border border-warning/20 bg-warning/5 px-3 py-2">
                <div className="text-sm font-medium text-text-primary">{v.name}</div>
                <div className="text-[10px] text-text-muted">{v.reason}</div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
